import {TaskState, TaskType} from '../../shared/task';
import {useEffect, useRef} from 'react';
import {createApiQuery} from './api';
import {GetTask, GetTaskResponse} from '../../shared/api/definitions/tasks';
import {ObjectId} from 'bson';

export const TaskStatusQueryKeys = {
  all: ['taskStatus'],
  task: (taskType: TaskType | undefined) => [
    ...TaskStatusQueryKeys.all,
    taskType,
  ],
} as const;

const useTaskQuery = createApiQuery(
  GetTask,
  ({type}) => TaskStatusQueryKeys.task(type),
  {
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      // Refetch more often while the task is pending or running.
      if (state === 'pending' || state === 'running') {
        return 5_000;
      }

      return 60_000;
    },
  }
);

export function useTask(
  params: Parameters<typeof useTaskQuery>[0],
  {
    onTaskComplete,
    ...options
  }: Parameters<typeof useTaskQuery>[1] & {
    onTaskComplete?: (task: NonNullable<GetTaskResponse>) => void;
  } = {}
) {
  const task = useRef<{id: ObjectId | 'no_task'; state: TaskState}>();
  const taskQuery = useTaskQuery(params, options);
  const currentTask = taskQuery.data;

  useEffect(() => {
    const previousTask = task.current;
    if (!previousTask && taskQuery.isSuccess) {
      // Customer has no previous tasks, so need this to detect completion of next task
      task.current = currentTask ?? {id: 'no_task', state: 'done'};
      return;
    }

    const isCurrentTaskComplete =
      currentTask?.state !== 'pending' && currentTask?.state !== 'running';

    // Skip processing when no previous tasks exist or current task is not complete
    if (!previousTask || !currentTask || !isCurrentTaskComplete) {
      return;
    }

    // Detect task completion and trigger onTaskComplete if different task, or state changed from in-progress
    const isDifferentTask = !currentTask.id.equals(previousTask.id);
    const isPreviousTaskLoading =
      previousTask.state === 'pending' || previousTask.state === 'running';

    // Trigger callback only on actual completion transitions
    if (isPreviousTaskLoading || isDifferentTask) {
      onTaskComplete?.(currentTask);
    }

    // Update reference to current task
    task.current = {id: currentTask.id, state: currentTask.state};
  }, [currentTask, onTaskComplete, taskQuery.isSuccess]);

  return {
    taskQuery,
    isTaskInProgress:
      currentTask?.state === 'pending' || currentTask?.state === 'running',
  };
}
